import React, { useState, useEffect } from "react";
import { onSnapshot, collection } from "firebase/firestore";
import { db } from "../firebase"; // Adjust the path based on your structure
import * as XLSX from "xlsx"; // Importing the xlsx package

const Earnings = () => {
  const [expanded, setExpanded] = useState("");
  const [filters, setFilters] = useState({
    store: "",
    customer: "",
    deliveryPartner: "",
    dateFrom: "",
    dateTo: "",
  });

  const [orders, setOrders] = useState([]);

  // Real-time fetch from Firebase
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "orders"), (snapshot) => {
      const orderList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrders(orderList);
    });
    return () => unsubscribe();
  }, []);

  const handleToggle = (key) => {
    setExpanded(expanded === key ? "" : key);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const filteredOrders = orders.filter((order) => {
    const { store, customer, deliveryPartner, dateFrom, dateTo } = filters;
    const matchesDate =
      (!dateFrom || new Date(order.date) >= new Date(dateFrom)) &&
      (!dateTo || new Date(order.date) <= new Date(dateTo));
    const matchesFilters =
      (!store || order.store?.toLowerCase().includes(store.toLowerCase())) &&
      (!customer || order.customer?.toLowerCase().includes(customer.toLowerCase())) &&
      (!deliveryPartner || order.deliveryPartner?.toLowerCase().includes(deliveryPartner.toLowerCase())) &&
      matchesDate;

    return matchesFilters;
  });

  const total = filteredOrders.reduce((acc, o) => acc + (o.amount || 0), 0);
  const commission = filteredOrders.reduce((acc, o) => acc + (o.commission || 0), 0);
  const storePayout = filteredOrders.reduce((acc, o) => acc + (o.storePayout || 0), 0);
  const deliveryPayout = filteredOrders.reduce((acc, o) => acc + (o.deliveryPayout || 0), 0);
  const deliveryFee = filteredOrders.reduce((acc, o) => acc + (o.deliveryCharge || 0), 0);
  const tax = filteredOrders.reduce((acc, o) => acc + (o.tax || 0), 0);
  const netProfit = commission + deliveryFee - deliveryPayout;

  const summary = [
    {
      key: "Total Revenue",
      value: total,
      details: "Total amount received from all orders.",
    },
    {
      key: "Commission Earned",
      value: commission,
      details: "Commission from each order.",
    },
    {
      key: "Delivery Charges Collected",
      value: deliveryFee,
      details: "Total delivery fees collected from users.",
    },
    {
      key: "Store Payouts",
      value: storePayout,
      details: "Paid to stores after commission deduction.",
    },
    {
      key: "Delivery Payouts",
      value: deliveryPayout,
      details: "Amount paid to delivery partners.",
    },
    {
      key: "Taxes",
      value: tax,
      details: "Total GST/taxes from orders.",
    },
    {
      key: "Net Profit",
      value: netProfit,
      details: `Commission (₹${commission}) + Delivery Charges (₹${deliveryFee}) - Delivery Payouts (₹${deliveryPayout})`,
    },
  ];

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredOrders);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, "OrderDetails.xlsx");
  };

  return (
    <div>
      <h2>Earnings Dashboard</h2>

      {/* Summary Boxes */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "20px" }}>
        {summary.map((box) => (
          <div
            key={box.key}
            onClick={() => handleToggle(box.key)}
            style={{
              border: "1px solid #ccc",
              padding: "25px", // Increased padding inside each box
              borderRadius: "8px",
              width: "240px",
              background: expanded === box.key ? "#f1f1f1" : "#fff",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              transition: "all 0.3s ease-in-out",
            }}
          >
            <strong>{box.key}</strong>
            <div style={{ fontSize: "18px", marginTop: "10px" }}>₹{box.value || 0}</div>
            {expanded === box.key && (
              <div style={{ fontSize: "13px", marginTop: "10px", color: "#555" }}>
                {box.details}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Filters */}
      <h3>Filter Summary & Orders</h3>
      <div style={{ display: "flex", gap: "15px", marginBottom: "30px", flexWrap: "wrap" }}>
        <input name="store" placeholder="Store Name" onChange={handleChange} />
        <input name="customer" placeholder="Customer Name" onChange={handleChange} />
        <input name="deliveryPartner" placeholder="Delivery Partner" onChange={handleChange} />
        <input name="dateFrom" type="date" onChange={handleChange} />
        <input name="dateTo" type="date" onChange={handleChange} />
      </div>

      {/* Excel Download Button */}
      <button onClick={handleDownloadExcel} style={{ padding: "12px 25px", marginBottom: "20px", cursor: "pointer", fontSize: "16px", backgroundColor: "#4CAF50", color: "#fff", border: "none", borderRadius: "5px" }}>
        Download Order Details as Excel
      </button>

      {/* Order Details */}
      <h3>Order Details</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr>
              {[
                "Order ID",
                "Customer",
                "Store",
                "Delivery Partner",
                "Date",
                "Amount",
                "Commission",
                "Store Payout",
                "Delivery Charge",
                "Delivery Payout",
                "Tax",
              ].map((head) => (
                <th
                  key={head}
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px", // Added more padding to table headers
                    background: "#f8f8f8",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={11} style={{ textAlign: "center", padding: "20px" }}>
                  No orders available.
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>{order.id || "-"}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>{order.customer || "-"}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>{order.store || "-"}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>{order.deliveryPartner || "-"}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>{order.date || "-"}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>₹{order.amount || 0}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>₹{order.commission || 0}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>₹{order.storePayout || 0}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>₹{order.deliveryCharge || 0}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>₹{order.deliveryPayout || 0}</td>
                  <td style={{ border: "1px solid #ddd", padding: "12px" }}>₹{order.tax || 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Earnings;
